import { Anchor, List, Text } from '@mantine/core';

import { LegalPage, LegalSection } from '@/components/organisms';

export function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy Policy"
      intro="This policy explains how Assay collects, uses, and protects your personal data. Assay is a product of Goldseam AI Consulting Ltd, a company registered in England & Wales (company number 16896682). We are committed to handling your data transparently and in accordance with UK GDPR and the Data Protection Act 2018."
    >
      <LegalSection heading="1. Who we are">
        <Text size="sm">
          <strong>Data controller:</strong> Goldseam AI Consulting Ltd, registered in England &
          Wales, company number 16896682.
        </Text>
        <Text size="sm">
          For privacy enquiries contact us at{' '}
          <Anchor href="mailto:privacy@runassay.app" size="sm">
            privacy@runassay.app
          </Anchor>
          .
        </Text>
      </LegalSection>

      <LegalSection heading="2. What data we collect">
        <Text size="sm">We collect two categories of personal data:</Text>
        <List size="sm" spacing={4}>
          <List.Item>
            <strong>Account data</strong> - your name and email address. Authentication is handled
            entirely by Clerk; Assay does not store your password or any other authentication
            credentials.
          </List.Item>
          <List.Item>
            <strong>Contact data</strong> - information about third parties (your contacts) that you
            import or enter into Assay, including names, email addresses, job titles, companies,
            notes, relationship grades, and outreach history. You are the data controller for this
            data; we act as your data processor. See our{' '}
            <Anchor href="/dpa" size="sm">
              Data Processing Agreement
            </Anchor>{' '}
            for details.
          </List.Item>
          <List.Item>
            <strong>Usage data</strong> - log data, error reports (via Sentry), and cookieless usage
            analytics (via Plausible) about how you use the service, collected to improve the
            product and diagnose issues.
          </List.Item>
        </List>
      </LegalSection>

      <LegalSection heading="3. Why we collect it and how we use it">
        <Text size="sm">
          We use your data solely to provide and improve the Assay service. Specifically:
        </Text>
        <List size="sm" spacing={8}>
          <List.Item>
            <strong>Account data</strong> (name, email address) - used to authenticate you, manage
            your subscription, and communicate service updates.{' '}
            <strong>Lawful basis: contract performance</strong> (Article 6(1)(b)) - processing is
            necessary to provide the service you have subscribed to.
          </List.Item>
          <List.Item>
            <strong>Contact data displayed in the app</strong> (contacts, grades, outreach history)
            - used to provide the core relationship-management features of the service.{' '}
            <strong>Lawful basis: contract performance</strong> (Article 6(1)(b)) - necessary to
            deliver the features you have paid for.
          </List.Item>
          <List.Item>
            <strong>Contact data sent to the Anthropic API</strong> - contact names, job titles, and
            any notes you have written about a contact may be included in prompts sent to Anthropic
            to generate outreach drafts on your behalf. <strong>This is explicit.</strong>{' '}
            <strong>Lawful basis: contract performance</strong> (Article 6(1)(b)) - generating
            personalised drafts is the core feature of the service.
          </List.Item>
          <List.Item>
            <strong>Contact data sent to enrichment providers</strong> (Exa, Tavily) - contact names
            and company names are sent to enrichment APIs to supplement your contact records with
            publicly available professional information.{' '}
            <strong>Lawful basis: legitimate interests</strong> (Article 6(1)(f)) - our legitimate
            interest in providing a richer contact management experience, and your interest as a
            subscriber in having up-to-date professional data about your network.
          </List.Item>
          <List.Item>
            <strong>Usage and analytics data</strong> - error reports (via Sentry) and cookieless
            usage analytics (via Plausible) used to diagnose issues and improve the product.
            Plausible does not use cookies, does not collect IP addresses, and does not track
            individuals across sessions or sites.{' '}
            <strong>Lawful basis: legitimate interests</strong> (Article 6(1)(f)) - our legitimate
            interest in understanding service usage and improving the product. You can object to
            this processing at any time (see Section 6).
          </List.Item>
          <List.Item>
            <strong>Billing records retained after account closure</strong> - invoices and payment
            records kept after you close your account.{' '}
            <strong>Lawful basis: legal obligation</strong> (Article 6(1)(c)) - UK tax law requires
            us to retain financial records for 7 years.
          </List.Item>
        </List>
        <Text size="sm">We do not sell your data or use it to profile you for advertising.</Text>
      </LegalSection>

      <LegalSection heading="4. Third-party services that receive your data">
        <Text size="sm">
          We share data with the following sub-processors in order to operate the service:
        </Text>
        <List size="sm" spacing={6}>
          <List.Item>
            <strong>Clerk</strong> (auth.clerk.com) - authentication and user management. Receives
            your name and email address. Clerk is certified under the EU-US Data Privacy Framework.
          </List.Item>
          <List.Item>
            <strong>Neon</strong> (neon.tech) - managed Postgres database. Stores all account and
            contact data. Data is stored in the EU (AWS eu-west-1 or equivalent). Connections are
            encrypted in transit and at rest.
          </List.Item>
          <List.Item>
            <strong>Anthropic API</strong> (anthropic.com) - AI message generation. Contact names,
            notes, and any context you provide are sent to Anthropic's API to generate outreach
            drafts. Anthropic's API data handling policy applies. Anthropic does not use API
            submissions to train its models by default.
          </List.Item>
          <List.Item>
            <strong>Trigger.dev</strong> - background job orchestration. Payloads may include
            contact identifiers for task routing. Data is not persisted beyond job execution.
            Infrastructure is hosted in the EU (AWS eu-central-1).
          </List.Item>
          <List.Item>
            <strong>Exa</strong> (exa.ai) and <strong>Tavily</strong> (tavily.com) - contact
            enrichment. Contact names and company names are sent to these APIs to retrieve publicly
            available professional information. Data is used only for enrichment and is not retained
            by these providers beyond the API response.
          </List.Item>
          <List.Item>
            <strong>Netlify</strong> - serverless function hosting. API requests are processed on
            Netlify's infrastructure, hosted in the UK (AWS eu-west-2, London).
          </List.Item>
          <List.Item>
            <strong>Sentry</strong> (sentry.io) - error monitoring and crash reporting. Sentry
            receives error stack traces and diagnostic context (e.g. page URL, browser version) when
            the application encounters an error. Personal data in error payloads is scrubbed where
            possible; Sentry data is stored in the EU.
          </List.Item>
          <List.Item>
            <strong>Plausible Analytics</strong> (plausible.io) - cookieless usage analytics.
            Receives aggregate, anonymous data about page views and feature usage. Plausible does
            not use cookies, does not store IP addresses, and does not track individuals. No
            personal data is transferred. Infrastructure is hosted in the EU (Germany).
          </List.Item>
        </List>
        <Text size="sm">
          We require all sub-processors to implement appropriate security measures and to process
          data only as instructed.
        </Text>
      </LegalSection>

      <LegalSection heading="5. Data retention">
        <Text size="sm">
          We retain your data for as long as your account is active. Specifically:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            <strong>Account data</strong> is retained until you delete your account.
          </List.Item>
          <List.Item>
            <strong>Contact data, grades, and outreach history</strong> are retained until you
            delete the contact or close your account.
          </List.Item>
          <List.Item>
            <strong>Contacts marked for removal</strong> (where you select "Remove" in the grading
            flow) have their record soft-deleted. You can request hard deletion at any time.
          </List.Item>
          <List.Item>
            <strong>Backup data</strong> is retained for up to 30 days after deletion.
          </List.Item>
        </List>
        <Text size="sm">
          On account closure, all personal data is deleted within 30 days, except where we are
          required to retain records by law (e.g. billing records for up to 7 years under UK tax
          law).
        </Text>
      </LegalSection>

      <LegalSection heading="6. Your rights under UK GDPR">
        <Text size="sm">
          As a UK data subject you have the following rights. To exercise any of them, email{' '}
          <Anchor href="mailto:privacy@runassay.app" size="sm">
            privacy@runassay.app
          </Anchor>
          .
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            <strong>Right of access</strong> - you can request a copy of all personal data we hold
            about you.
          </List.Item>
          <List.Item>
            <strong>Right to rectification</strong> - you can ask us to correct inaccurate data.
          </List.Item>
          <List.Item>
            <strong>Right to erasure</strong> - you can ask us to delete your account and all
            associated data.
          </List.Item>
          <List.Item>
            <strong>Right to data portability</strong> - you can request an export of your contact
            data in a machine-readable format (CSV or JSON).
          </List.Item>
          <List.Item>
            <strong>Right to restrict processing</strong> - you can ask us to stop processing your
            data in certain circumstances.
          </List.Item>
          <List.Item>
            <strong>Right to object</strong> - you can object to processing based on legitimate
            interests.
          </List.Item>
        </List>
        <Text size="sm">
          We will respond to all requests within one calendar month. In complex cases we may extend
          this by a further two months, in which case we will notify you within the first month. You
          also have the right to lodge a complaint with the Information Commissioner's Office (ICO)
          at{' '}
          <Anchor href="https://ico.org.uk" size="sm" target="_blank" rel="noopener noreferrer">
            ico.org.uk
          </Anchor>
          .
        </Text>
      </LegalSection>

      <LegalSection heading="7. Cookies">
        <Text size="sm">
          Assay uses only essential cookies required for authentication (set by Clerk). We do not
          use tracking, advertising, or analytics cookies. You cannot opt out of essential cookies
          without losing access to the service, as they are necessary for login sessions.
        </Text>
      </LegalSection>

      <LegalSection heading="8. Security">
        <Text size="sm">
          We use industry-standard security practices: encrypted connections (TLS), encrypted
          storage, access controls, and regular security reviews. No system is 100% secure; if you
          believe your data has been compromised, contact us immediately at{' '}
          <Anchor href="mailto:privacy@runassay.app" size="sm">
            privacy@runassay.app
          </Anchor>
          .
        </Text>
      </LegalSection>

      <LegalSection heading="9. Changes to this policy">
        <Text size="sm">
          We may update this policy as the service evolves. Material changes will be communicated by
          email or in-app notice with at least 14 days' notice before the effective date. If you do
          not agree to the updated policy, you may close your account before the effective date. If
          you continue to use the service after the effective date, you will be taken to have
          accepted the updated policy.
        </Text>
      </LegalSection>

      <LegalSection heading="10. Data Protection Officer">
        <Text size="sm">
          Goldseam AI Consulting Ltd has assessed its processing activities against the criteria set
          out in UK GDPR Article 37 and has determined that it is not required to appoint a Data
          Protection Officer at this time. Privacy enquiries should be directed to{' '}
          <Anchor href="mailto:privacy@runassay.app" size="sm">
            privacy@runassay.app
          </Anchor>
          .
        </Text>
      </LegalSection>

      <Text size="sm" c="dimmed">
        Last updated: 23 March 2026. Goldseam AI Consulting Ltd, company number 16896682, registered
        in England & Wales.
      </Text>
    </LegalPage>
  );
}
